import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase";
import { useUser } from "./UserContext";

export type PatientDirectoryRow = {
  id: string;
  praticien_id: string;
  full_name: string;
  phone: string;
  patient_identifier?: string | null;
  preferred_practitioner?: string | null;
  comments?: string | null;
  created_at?: string;
  updated_at?: string;
};

type PatientDirectoryContextValue = {
  activePractitionerId: string | null;
  setActivePractitionerId: (id: string | null) => void;
  loadDirectory: (praticienId: string) => Promise<void>;
  getDisplayNameForPhone: (
    phone: string,
    praticienId?: string | null
  ) => string | undefined;
  getDirectoryFor: (praticienId?: string | null) => PatientDirectoryRow[];
  upsertLocalEntry: (entry: PatientDirectoryRow) => void;
  removeLocalEntry: (entry: PatientDirectoryRow) => void;
};

const PatientDirectoryContext = createContext<PatientDirectoryContextValue | undefined>(
  undefined
);

function normalizePhone(raw: string) {
  const trimmed = raw.trim();
  const withoutPrefix = trimmed.replace(/^whatsapp:/i, "");
  return withoutPrefix.replace(/\s+/g, "");
}

function phoneKeys(phone: string) {
  const normalized = normalizePhone(phone);
  return [normalized, `whatsapp:${normalized}`];
}

function formatDisplayName(entry: PatientDirectoryRow) {
  if (entry.patient_identifier) {
    return `${entry.full_name} (${entry.patient_identifier})`;
  }
  return entry.full_name;
}

export function PatientDirectoryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useUser();
  const [activePractitionerId, setActivePractitionerId] = useState<string | null>(null);
  const [directories, setDirectories] = useState<
    Record<string, Record<string, PatientDirectoryRow>>
  >({});
  const loadedIdsRef = useRef<Set<string>>(new Set());
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Auto sélection du praticien courant pour les comptes praticiens
  useEffect(() => {
    if (!user) {
      setDirectories({});
      loadedIdsRef.current = new Set();
      setActivePractitionerId(null);
      return;
    }

    if (user.role !== "admin") {
      setActivePractitionerId(user.id);
    }
  }, [user]);

  // Chargement initial (une seule requête par praticien)
  const loadDirectory = useCallback(
    async (praticienId: string) => {
      if (!praticienId) return;
      if (loadedIdsRef.current.has(praticienId)) return;

      const { data, error } = await supabase
        .from("patients_directory")
        .select(
          "id, praticien_id, full_name, phone, patient_identifier, preferred_practitioner, comments, created_at, updated_at"
        )
        .eq("praticien_id", praticienId);

      loadedIdsRef.current.add(praticienId);

      if (error || !data) return;

      setDirectories((prev) => {
        const next = { ...prev };
        const map: Record<string, PatientDirectoryRow> = {};
        for (const entry of data as PatientDirectoryRow[]) {
          for (const key of phoneKeys(entry.phone)) {
            map[key] = entry;
          }
        }
        next[praticienId] = map;
        return next;
      });
    },
    []
  );

  // Réception des mises à jour realtime pour le praticien actif
  useEffect(() => {
    if (!activePractitionerId) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    // S'assure que l'annuaire de ce praticien est chargé au moins une fois
    loadDirectory(activePractitionerId);

    const channel = supabase
      .channel(`patients-directory-${activePractitionerId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "patients_directory",
          filter: `praticien_id=eq.${activePractitionerId}`,
        },
        (payload) => {
          const entry = (payload.new || payload.old) as PatientDirectoryRow;
          if (!entry) return;

          if (payload.eventType === "DELETE") {
            removeEntry(entry);
          } else {
            upsertEntry(entry);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [activePractitionerId, loadDirectory]);

  const upsertEntry = useCallback((entry: PatientDirectoryRow) => {
    setDirectories((prev) => {
      const existing = prev[entry.praticien_id] ? { ...prev[entry.praticien_id] } : {};
      for (const key of phoneKeys(entry.phone)) {
        existing[key] = entry;
      }
      return { ...prev, [entry.praticien_id]: existing };
    });
  }, []);

  const removeEntry = useCallback((entry: PatientDirectoryRow) => {
    setDirectories((prev) => {
      const current = prev[entry.praticien_id];
      if (!current) return prev;
      const updated = { ...current };
      for (const key of phoneKeys(entry.phone)) {
        delete updated[key];
      }
      return { ...prev, [entry.praticien_id]: updated };
    });
  }, []);

  const getDisplayNameForPhone = useCallback(
    (phone: string, praticienId?: string | null) => {
      const targetId = praticienId || activePractitionerId;
      if (!targetId) return undefined;

      const dir = directories[targetId];
      if (!dir) return undefined;

      for (const key of phoneKeys(phone)) {
        const entry = dir[key];
        if (entry) return formatDisplayName(entry);
      }

      return undefined;
    },
    [activePractitionerId, directories]
  );

  const getDirectoryFor = useCallback(
    (praticienId?: string | null) => {
      const targetId = praticienId || activePractitionerId;
      if (!targetId) return [];
      const dir = directories[targetId];
      if (!dir) return [];

      const seen = new Set<string>();
      const list: PatientDirectoryRow[] = [];
      for (const entry of Object.values(dir)) {
        if (seen.has(entry.id)) continue;
        seen.add(entry.id);
        list.push(entry);
      }

      return list.sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
    [activePractitionerId, directories]
  );

  const value = useMemo(
    () => ({
      activePractitionerId,
      setActivePractitionerId,
      loadDirectory,
      getDisplayNameForPhone,
      getDirectoryFor,
      upsertLocalEntry: upsertEntry,
      removeLocalEntry: removeEntry,
    }),
    [
      activePractitionerId,
      getDisplayNameForPhone,
      getDirectoryFor,
      loadDirectory,
      removeEntry,
      upsertEntry,
    ]
  );

  return (
    <PatientDirectoryContext.Provider value={value}>
      {children}
    </PatientDirectoryContext.Provider>
  );
}

export function usePatientDirectory() {
  const ctx = useContext(PatientDirectoryContext);
  if (!ctx) throw new Error("usePatientDirectory must be used within PatientDirectoryProvider");
  return ctx;
}
